import { useLanguage } from "../contexts/LanguageContext";
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../Components/ui/button';
import { Card, CardContent } from '../Components/ui/card';
import {
  ArrowRight,
  Battery,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  CloudSun,
  Globe2,
  Monitor,
  RefreshCcw,
  ShieldCheck,
  Settings,
  Wifi,
  Wrench,
  Newspaper,
  ShieldAlert,
  FileText,
  HelpCircle,
  MessageCircle,
  Zap,
  PenTool
} from 'lucide-react';

const highlights = [
  {
    title: '7.5-inch e-paper display',
    description:
      'A glare-free screen designed for comfortable all-day reading with low energy use and excellent visibility in bright rooms.',
    icon: Monitor
  },
  {
    title: 'At-a-glance essentials',
    description:
      'See schedules, weather, economy, entertainment, and other card components from the full library in one calm view without app switching.',
    icon: CalendarDays
  },
  {
    title: 'Low-distraction experience',
    description:
      'No social feeds, no noisy alerts, and no constant glow. Kylendar V2 is made to keep focus on what matters next.',
    icon: ShieldCheck
  },
  {
    title: 'Sustainable by design',
    description:
      'Electronic ink only refreshes when content changes, helping reduce power draw while maintaining clear readability.',
    icon: Battery
  }
];

const setupFlow = [
  'Connect power using the included USB-C cable and start the on-screen setup wizard.',
  'Verify and bind your device with the required device credentials.',
  'Create your first workspace (one workspace is bound to one device and can contain multiple pages).',
  'Select your country/city and preferred language for localized content.',
  'Connect to a 2.4GHz Wi-Fi network for live updates and cloud sync.',
  'Complete initial display preferences and finish first-time synchronization.',
  'Place the device on desk or mount it on the wall for daily visibility.'
];

const supportPoints = [
  {
    title: 'Wireless connectivity',
    description: 'Supports standard 2.4GHz Wi-Fi networks commonly used in homes and offices.',
    icon: Wifi
  },
  {
    title: 'Automatic updates',
    description: 'Firmware updates are delivered over Wi-Fi, with manual update checks available in settings.',
    icon: Settings
  },
  {
    title: 'Reliable daily operation',
    description: 'Built for always-on household use with clear status information and simple troubleshooting paths.',
    icon: Wrench
  }
];

const featureDetails = [
  {
    title: 'Calendar at a glance',
    description:
      'Keep date and schedule context visible in one place so your next commitment is always easy to check.',
    icon: CalendarDays
  },
  {
    title: 'Weather visibility',
    description:
      'Review weather updates directly on the display before leaving home or planning the rest of your day.',
    icon: CloudSun
  },
  {
    title: 'Headline awareness',
    description:
      'Stay informed with essential news without opening social feeds or entering a scrolling workflow.',
    icon: Newspaper
  },
  {
    title: 'World clock support',
    description:
      'Track multiple time zones for remote teams, global family communication, and cross-region planning.',
    icon: Clock3
  },
  {
    title: 'Mount-ready form factor',
    description:
      'Use as a desk companion or mount to a wall using the rear mounting point for shared household visibility.',
    icon: CheckCircle2
  },
  {
    title: 'Readable all day',
    description:
      'E-paper technology is designed for comfortable viewing in bright spaces while keeping visual output clean.',
    icon: Monitor
  }
];

const supportSnapshot = [
  {
    title: 'Setup in minutes',
    body: 'Connect USB-C power, follow the onboarding wizard, then choose display preferences.',
    icon: Settings
  },
  {
    title: 'Network compatibility',
    body: 'Works with standard 2.4GHz Wi-Fi environments used by most home and office routers.',
    icon: Wifi
  },
  {
    title: 'OTA update path',
    body: 'Software updates can be delivered over Wi-Fi, with manual checks available from settings.',
    icon: RefreshCcw
  },
  {
    title: 'Warranty and support',
    body: 'Backed by a 1-year warranty and support contact path for troubleshooting and service requests.',
    icon: ShieldAlert
  }
];

const layoutCapabilities = [
  {
    title: 'Drag-and-drop calendar canvas',
    detail:
      'Build custom calendar screens by dragging cards onto the layout and arranging information blocks visually.',
    icon: Monitor
  },
  {
    title: 'Pixel-precise positioning',
    detail:
      'Refine spacing and alignment to keep cards balanced, readable, and optimized for e-paper viewing distance.',
    icon: Settings
  },
  {
    title: 'Rich widget composition',
    detail:
      'Mix schedules, weather, economy, entertainment, and many other cards to create layouts tailored to family, work, or team routines.',
    icon: CalendarDays
  },
  {
    title: 'AI-assisted layout acceleration',
    detail:
      'Use AI-assisted generation to speed up first drafts, then fine-tune card placement manually for final polish.',
    icon: RefreshCcw
  }
];

const aiMagicHighlights = [
  {
    title: 'Natural-language layout generation',
    detail:
      'Describe the scenario in plain language and let AI Magic generate a first layout draft without manual card-by-card setup.',
    icon: FileText
  },
  {
    title: 'Component-aware matching',
    detail:
      'AI maps intent to relevant card types, then assembles calendar, weather, content, data, and other card components into a usable dashboard structure.',
    icon: CalendarDays
  },
  {
    title: 'Overlap-safe composition',
    detail:
      'The generated result follows structured element output and layout constraints, reducing overlap and speeding up first-pass readability.',
    icon: ShieldCheck
  },
  {
    title: 'Editable after generation',
    detail:
      'AI output is a starting point, not a lock-in. Continue with full drag-and-drop adjustment, card sizing, and style fine-tuning.',
    icon: Wrench
  }
];

const aiMagicFlow = [
  'Open AI Magic in the editor and describe your target scene, such as family schedule board, office timeline, or mixed information dashboard.',
  'AI generates a structured layout draft with placed elements, selected components, and size-aware positioning on the 800 x 480 canvas.',
  'Review generated cards, then refine card size, frame style, spacing, and data binding according to your workspace requirements.',
  'Save the workspace-device binding; the latest design syncs to cloud automatically and is pulled by the e-paper device on its next online refresh.'
];

const editorInterfaceOverview = [
  {
    title: 'Top Action Bar',
    detail:
      'Central control area for workspace/page context, AI generation trigger, and save or publish actions during layout work.',
    icon: Settings
  },
  {
    title: 'Left Component Library',
    detail:
      'Category-based widget library where cards can be searched and dragged onto the canvas for rapid composition.',
    icon: CalendarDays
  },
  {
    title: 'Center Live Canvas',
    detail:
      'WYSIWYG editing on an 800 x 480 board with grid-aware placement so visual editing matches final device output.',
    icon: Monitor
  },
  {
    title: 'Right Properties Panel',
    detail:
      'Component-level configuration for card size, position, frame style, spacing, and data fields without writing code.',
    icon: Wrench
  }
];

const cardStyleControls = [
  'Frame visibility on each card',
  'Dashed or solid frame presentation',
  'Corner radius adjustment',
  'Frame thickness control',
  'Frame margin spacing control',
  'Consistent visual rules across multiple cards'
];

const canvasAndSizeProfiles = [
  { size: 'Size 0 (Full Canvas)', pixels: '800 x 480', usage: 'Full-screen layouts such as month calendar dashboards.' },
  { size: 'Size 1 (Half Vertical)', pixels: '400 x 480', usage: 'Left-right split dashboards and dual-column planning.' },
  { size: 'Size 2 (Half Horizontal)', pixels: '800 x 240', usage: 'Top-bottom split layouts for timeline plus summary.' },
  { size: 'Size 3 (Quarter Block)', pixels: '400 x 240', usage: 'Balanced cards for weather, news, schedule, and sports.' },
  { size: 'Size 4 (Narrow Block)', pixels: '200 x 240', usage: 'Compact side cards and dense information stacks.' },
  { size: 'Size 5 (Mini Block)', pixels: '200 x 120', usage: 'Status micro-cards such as power or connectivity indicators.' }
];

const configurationGuide = [
  {
    key: 'Layout and placement',
    value:
      'Cards are positioned by x/y coordinates on an 800 x 480 canvas, with optional snap-to-grid editing for clean alignment.'
  },
  {
    key: 'Card sizing',
    value:
      'Each component supports a subset of size profiles (0-5), allowing consistent responsive composition within the fixed e-paper canvas.'
  },
  {
    key: 'Frame controls',
    value:
      'Common style settings include frame visibility, dashed style, corner radius, thickness, color, and margin to keep hierarchy clear.'
  },
  {
    key: 'Component data fields',
    value:
      'Cards expose feature-specific fields such as titles, symbols, event lists, schedules, cities, units, and custom text payloads.'
  },
  {
    key: 'Live updates',
    value:
      'Calendar, weather, and data widgets can refresh through connected services, while OTA deployment keeps layout updates operational.'
  }
];

const popularRecommendedCards = [
  { ids: ['week_schedule'], name: 'Week Schedule', description: 'Displays weekly planning blocks for ongoing schedule awareness.', image: '/kylendarV2/Calendars&Schedules/WeekSchedule.png' },
  { ids: ['today_schedule'], name: 'Today Schedule', description: 'Shows day-level events and appointments in a focused format.', image: '/kylendarV2/Calendars&Schedules/TodaySchedule.png' },
  { ids: ['news'], name: 'News Card', description: 'Headline feed block for fast information scans without distraction loops.', image: '/kylendarV2/News&Content/NewsCard.png' },
  { ids: ['sticky_note'], name: 'Sticky Note Card', description: 'Pinned note component for priority messages and personal reminders.', image: '/kylendarV2/Calendars&Schedules/StickyNoteCard.png' },
  { ids: ['daily_focus'], name: 'Daily Focus Card', description: 'Single-focus prompt to define one clear daily priority.', image: '/kylendarV2/Calendars&Schedules/DailyFocusCard.png' },
  { ids: ['weather_today'], name: 'Weather Today', description: 'Current weather condition summary for immediate planning.', image: '/kylendarV2/Weather/WeatherToday.png' },
  {
    ids: ['weekly_weather'],
    name: 'Weekly Weather',
    description: 'Multi-day weather forecast card for ahead-of-time decisions.',
    images: [
      '/kylendarV2/Weather/WeeklyWeather1.png',
      '/kylendarV2/Weather/WeeklyWeather2.png'
    ]
  }
];

const componentReferenceSections = [
  {
    category: 'Sports & Events',
    items: [
      { ids: ['nfl_card'], name: 'NFL Card', description: 'Shows game highlights and team-focused NFL updates.', image: '/kylendarV2/Sports&Events/NFL_Card.png' },
      { ids: ['nba_card'], name: 'NBA Card', description: 'Displays NBA game and team information in a compact card.', image: '/kylendarV2/Sports&Events/NBACard.png' },
      { ids: ['sports_table'], name: 'Sports Table', description: 'Provides ranking or table-style sports competition views.', image: '/kylendarV2/Sports&Events/SportsTable.png' },
      { ids: ['nba_standings', 'nba_standings_card'], name: 'NBA Standings', description: 'Tracks conference standings with East/West context.', image: '/kylendarV2/Sports&Events/NBAStandings.png' }
    ]
  },
  {
    category: 'Calendars & Schedules',
    items: [
      { ids: ['today_schedule'], name: 'Today Schedule', description: 'Shows day-level events and appointments in a focused format.', image: '/kylendarV2/Calendars&Schedules/TodaySchedule.png' },
      { ids: ['week_schedule'], name: 'Week Schedule', description: 'Displays weekly planning blocks for ongoing schedule awareness.', image: '/kylendarV2/Calendars&Schedules/WeekSchedule.png' },
      { ids: ['month_calendar'], name: 'Month Calendar', description: 'Monthly grid view with date context and optional event marks.', image: '/kylendarV2/Calendars&Schedules/MonthCalendar.png' },
      { ids: ['simple_calendar', 'simpleCalendar'], name: 'Simple Calendar', description: 'Lightweight calendar block for minimal, clean layouts.', image: '/kylendarV2/Calendars&Schedules/SimpleCalendar.png' },
      { ids: ['threeSectionCalendar', 'three_section_card'], name: 'Three-Section Calendar', description: 'Segmented calendar presentation for multi-part schedule views.', image: '/kylendarV2/Calendars&Schedules/Three-SectionCalendar.png' },
      { ids: ['reminder_card'], name: 'Reminder Card', description: 'Task and reminder list with time-first readability.', image: '/kylendarV2/Calendars&Schedules/ReminderCard.png' },
      { ids: ['sticky_note'], name: 'Sticky Note Card', description: 'Pinned note component for priority messages and personal reminders.', image: '/kylendarV2/Calendars&Schedules/StickyNoteCard.png' },
      { ids: ['daily_focus'], name: 'Daily Focus Card', description: 'Single-focus prompt to define one clear daily priority.', image: '/kylendarV2/Calendars&Schedules/DailyFocusCard.png' }
    ]
  },
  {
    category: 'News & Content',
    items: [
      { ids: ['news'], name: 'News Card', description: 'Headline feed block for fast information scans without distraction loops.', image: '/kylendarV2/News&Content/NewsCard.png' },
      { ids: ['on_this_day'], name: 'On This Day', description: 'Highlights notable date-based historical facts and events.', image: '/kylendarV2/News&Content/OnThisDay.png' }
    ]
  },
  {
    category: 'Entertainment',
    items: [
      { ids: ['movie'], name: 'Movie Highlight', description: 'Movie discovery card with category-based content selection.', image: '/kylendarV2/Entertainment/MovieHighlight.png' },
      { ids: ['music_recommendation'], name: 'Music Recommendation', description: 'Music suggestion card for ambient entertainment context.', image: '/kylendarV2/Entertainment/MusicRecommendation.png' }
    ]
  },
  {
    category: 'Finance & Data',
    items: [
      { ids: ['stock_info_cards'], name: 'Stock Info Cards', description: 'Multi-symbol market snapshot cards for quick trend checks.', image: '/kylendarV2/Finance&Data/StockInfo.png' },
      { ids: ['graph'], name: 'Graph Card', description: 'Data trend chart for compact visual signal tracking.', image: '/kylendarV2/Finance&Data/LineGraph.png' },
      { ids: ['kline_chart'], name: 'K-Line Chart', description: 'Candlestick-style market movement visualization.', image: '/kylendarV2/Finance&Data/K-LineChart.png' },
      { ids: ['portfolio_summary'], name: 'Portfolio Summary', description: 'Aggregated personal portfolio performance and holdings overview.', image: '/kylendarV2/Finance&Data/PortfolioSummary.png' },
      { ids: ['economic_calendar'], name: 'Economic Calendar', description: 'Event timeline for macroeconomic releases and market moments.', image: '/kylendarV2/Finance&Data/EconomicCalendar.png' }
    ]
  },
  {
    category: 'Time Tracking',
    items: [
      {
        ids: ['rts'],
        name: 'RTS Countdown',
        description: 'Progress countdown widget for long-term project and milestone tracking.',
        images: [
          '/kylendarV2/TimeTracking/RTSCountdown1.png',
          '/kylendarV2/TimeTracking/RTSCountdown2.png',
          '/kylendarV2/TimeTracking/RTSCountdown3.png'
        ]
      },
      { ids: ['world_clock'], name: 'World Clock', description: 'Multi-timezone clock display (12-hour format).', image: '/kylendarV2/TimeTracking/WorldClock.png' }
    ]
  },
  {
    category: 'Weather',
    items: [
      { ids: ['weather_today'], name: 'Weather Today', description: 'Current weather condition summary for immediate planning.', image: '/kylendarV2/Weather/WeatherToday.png' },
      {
        ids: ['weekly_weather'],
        name: 'Weekly Weather',
        description: 'Multi-day weather forecast card for ahead-of-time decisions.',
        images: [
          '/kylendarV2/Weather/WeeklyWeather1.png',
          '/kylendarV2/Weather/WeeklyWeather2.png'
        ]
      },
      { ids: ['air_quality'], name: 'Air Quality', description: 'AQI and pollutant context for healthier daily planning.', image: '/kylendarV2/Weather/AirQuality.png' }
    ]
  },
  {
    category: 'Device status',
    items: [
      { ids: ['power_wifi'], name: 'Power & Wi-Fi', description: 'Battery, charging, and network state in one operational card.', image: '/kylendarV2/DeviceStatus/PowerWiFi.png' }
    ]
  },
  {
    category: "Children's World",
    items: [
      { ids: ['curriculum'], name: 'Curriculum', description: 'Weekly course schedule.', image: '/kylendarV2/Children\'sWorld/Curriculum.png' },
      { ids: ['curriculum_7day'], name: '7-Day Curriculum', description: 'Weekly schedule (Sun-Sat).', image: '/kylendarV2/Children\'sWorld/7-DayCurriculum.png' },
      { ids: ['daily_quote'], name: 'Daily Quote Card', description: 'Inspirational quotes for children.', image: '/kylendarV2/Children\'sWorld/DailyQuoteCard.png' },
      { ids: ['after_school_activity'], name: 'After-school Activity Card', description: 'Track extracurricular activities and progress.', image: '/kylendarV2/Children\'sWorld/After-schoolActivityCard.png' },
      { ids: ['exam_countdown'], name: 'Exam Countdown Card', description: 'Track days left for important exams.', image: '/kylendarV2/Children\'sWorld/ExamCountdownCard.png' },
      { ids: ['homework_card'], name: 'Homework Card', description: 'Daily homework tracker with subjects and time.', image: '/kylendarV2/Children\'sWorld/HomeworkCard.png' },
      { ids: ['hydration_card'], name: 'Hydration Card', description: 'Daily hydration progress and tips.', image: '/kylendarV2/Children\'sWorld/HydrationCard.png' }
    ]
  },
  {
    category: "Senior Citizens' Paradise",
    items: [
      { ids: ['routine_card'], name: 'Routine Card', description: 'Repeating daily routine structure for habit and rhythm tracking.', image: '/kylendarV2/SeniorCitizens\'Paradise/RoutineCard.png' },
      { ids: ['medication_card'], name: 'Medication Card', description: 'Medication schedule guidance for safer daily routines.', image: '/kylendarV2/SeniorCitizens\'Paradise/MedicationCard.png' },
      { ids: ['emergency_notice'], name: 'Emergency Notice', description: 'Important emergency contacts and rapid-reference details.', image: '/kylendarV2/SeniorCitizens\'Paradise/EmergencyNoticeCard.png' }
    ]
  },
  {
    category: 'Beauty & Health',
    items: [
      { ids: ['beauty_routine'], name: 'Beauty Routine', description: 'Step-based personal beauty workflow tracking.', image: '/kylendarV2/Beauty&Health/BeautyRoutineCard.png' },
      { ids: ['outFit', 'outfit_suggestion'], name: 'Outfit Suggestion', description: 'Daily outfit recommendation helper for practical planning.', image: '/kylendarV2/Beauty&Health/OutfitSuggestionCard.png' }
    ]
  }
];

const componentSizeMap = {
  nfl_card: '1, 3',
  nba_card: '1, 3',
  sports_table: '0, 1',
  nba_standings: '0, 1',
  nba_standings_card: '0, 1',
  football_card: '1, 3',
  today_schedule: '0, 1',
  week_schedule: '0, 1',
  month_calendar: '0',
  simple_calendar: '1, 3, 4',
  simpleCalendar: '1, 3, 4',
  threeSectionCalendar: '3, 4',
  reminder_card: '1, 3',
  routine_card: '1, 3',
  curriculum: '0',
  curriculum_7day: '0',
  three_section_card: '3, 4',
  news: '0, 1, 2, 3, 4',
  sticky_note: '1, 3',
  on_this_day: '1',
  daily_quote: '1, 3',
  daily_focus: '1',
  movie: '0',
  music_recommendation: '3',
  stock_info_cards: '0, 1, 3',
  graph: '0, 1, 3',
  kline_chart: '0',
  portfolio_summary: '1, 3',
  economic_calendar: '1, 3',
  rts: '0, 3, 4',
  time: '3, 4, 5',
  world_clock: '3, 4, 5',
  weather_today: '2, 3',
  weekly_weather: '1, 2',
  air_quality: '3',
  power_wifi: '5',
  homework_card: '1, 3',
  exam_countdown: '1, 3',
  after_school_activity: '1, 3',
  medication_card: '1',
  emergency_notice: '1',
  beauty_routine: '1, 3',
  hydration_card: '3',
  outFit: '3',
  outfit_suggestion: '3'
};

const componentConfigMap = {
  nfl_card: 'select one interested team and card border configuration.',
  nba_card: 'select one interested team and card border configuration.',
  sports_table: 'card border configuration.',
  nba_standings: 'enter season information and card border configuration.',
  nba_standings_card: 'enter season information and card border configuration.',
  football_card: 'league/team preferences plus card border configuration.',
  today_schedule: 'connect a third-party calendar (Google, iOS, Outlook) to display schedule information, or manually enter schedule details for display, and card border configuration.',
  week_schedule: 'connect a third-party calendar (Google, iOS, Outlook) to display schedule information and card border configuration.',
  month_calendar: 'click a date to customize schedule information and card border configuration.',
  simple_calendar: 'card border configuration.',
  simpleCalendar: 'card border configuration.',
  threeSectionCalendar: 'card border configuration.',
  three_section_card: 'card border configuration.',
  reminder_card: 'enter event time and name, choose an icon, and card border configuration.',
  routine_card: 'enter daily routine item name and time, choose an icon, and card border configuration.',
  curriculum: 'course name, course date, start and end time, and card border configuration.',
  curriculum_7day: 'course name, course date, start and end time, weekend interest class details, and card border configuration.',
  news: 'card border configuration.',
  sticky_note: 'event title, detail information, and card border configuration.',
  on_this_day: 'card border configuration.',
  daily_quote: 'select interested categories and card border configuration.',
  daily_focus: 'connect a third-party calendar (Google, iOS, Outlook) to display schedule information, or manually enter schedule details, manually input focus content and weather location information for weather display, and card border configuration.',
  movie: 'movie genre selection and card border configuration.',
  music_recommendation: 'music category selection and card border configuration.',
  stock_info_cards: 'enter interested stock symbols (ticker / symbol) and card border configuration.',
  graph: 'enter interested stock symbols (ticker / symbol) and card border configuration.',
  kline_chart: 'enter interested stock symbols (ticker / symbol) and card border configuration.',
  portfolio_summary: 'enter stock symbols, total cost (cost basis), and card border configuration.',
  economic_calendar: 'select interested event categories and card border configuration.',
  rts: 'event name, start time, end time, and card border configuration.',
  time: 'time format/timezone display fields with card border configuration and text styling.',
  world_clock: 'select country and city information and card border configuration.',
  weather_today: 'country, city, temperature unit display, and card border configuration.',
  weekly_weather: 'country, city, temperature unit display, and card border configuration.',
  air_quality: 'country, city, and card border configuration.',
  power_wifi: 'card border configuration.',
  homework_card: 'enter homework content, time, subject, expected completion time, choose an icon, and card border configuration.',
  exam_countdown: 'exam name, subject, date, and related details, and card border configuration.',
  after_school_activity: 'enter extracurricular activity name and event details, choose an icon, and card border configuration.',
  medication_card: 'enter medication time, medicine name and dosage for seniors, include caution notes, and card border configuration.',
  emergency_notice: 'enter emergency contact name, relationship, contact details, caution notes, and card border configuration.',
  beauty_routine: 'select Morning routine/Evening routine, use default skincare steps or add custom skincare steps and descriptions, and card border configuration.',
  hydration_card: 'select child age, enter child height and weight information, and card border configuration.',
  outFit: 'select country and city information and card border configuration.',
  outfit_suggestion: 'select country and city information and card border configuration.'
};

function getComponentSizes(ids) {
  const sizes = new Set();
  ids.forEach((id) => {
    const raw = componentSizeMap[id];
    if (!raw) return;
    raw.split(',').forEach((s) => sizes.add(s.trim()));
  });
  return sizes.size ? Array.from(sizes).sort((a, b) => Number(a) - Number(b)).join(', ') : 'Varies';
}

function getComponentConfigHint(ids) {
  for (const id of ids) {
    if (componentConfigMap[id]) return componentConfigMap[id];
  }
  return 'Component-specific data fields plus standard card border configuration.';
}

function optimizeComponentDescription(text) {
  const normalized = text.trim().replace(/\.$/, '');
  const rewrites = [
    { pattern: /^Shows /i, replace: 'Delivers ' },
    { pattern: /^Displays /i, replace: 'Surfaces ' },
    { pattern: /^Tracks /i, replace: 'Keeps track of ' },
    { pattern: /^Provides /i, replace: 'Brings ' }
  ];

  let improved = normalized;
  rewrites.forEach(({ pattern, replace }) => {
    if (pattern.test(improved)) improved = improved.replace(pattern, replace);
  });

  if (/calendar|schedule|reminder|routine/i.test(improved)) {
    return `${improved}, helping users stay on time with less planning friction.`;
  }
  if (/weather|air quality|aqi/i.test(improved)) {
    return `${improved}, so daily decisions can be made earlier and with more confidence.`;
  }
  if (/stock|graph|k-line|portfolio|economic|data/i.test(improved)) {
    return `${improved}, turning dense metrics into quick, decision-ready context.`;
  }
  if (/nfl|nba|sports|football|standings/i.test(improved)) {
    return `${improved}, keeping live game context visible without opening extra apps.`;
  }
  if (/homework|exam|curriculum|children/i.test(improved)) {
    return `${improved}, making family and study routines easier to coordinate.`;
  }
  if (/medication|emergency|senior/i.test(improved)) {
    return `${improved}, improving clarity and safety for everyday care routines.`;
  }
  return `${improved}, giving users faster clarity at a glance.`;
}

const multilingualSupport = [
  'United States (USA)',
  'United Kingdom (UK)',
  'Spain (ES)',
  'Italy (IT)',
  'France (FR)',
  'Germany (DE)',
  'Portugal (PT)',
  'Brazil (BR)',
  'Japan (JP)',
  'Mexico (MX)'
];

function IconBadge({ icon: Icon, large = false }) {
  return (
    <div className={`${large ? 'w-10 h-10 sm:w-11 sm:h-11' : 'w-9 h-9 sm:w-10 sm:h-10'} rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-[1px] shadow-sm transition-transform hover:scale-105`}>
      <div className="w-full h-full rounded-[11px] bg-white flex items-center justify-center text-emerald-700">
        <Icon size={large ? 20 : 18} strokeWidth={2.2} />
      </div>
    </div>
  );
}

export default function KylendarV2() {
  const { tx } = useLanguage();
  const [previewImage, setPreviewImage] = React.useState(null);
  const [galleryIndexes, setGalleryIndexes] = React.useState({});

  React.useEffect(() => {
    if (!previewImage) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setPreviewImage(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [previewImage]);

  const renderHeroBackground = () => (
    <div
      className="absolute inset-0 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: 'url(/poster4.jpg)' }}
    />
  );

  const getGalleryIndex = (galleryKey, total) => {
    const current = galleryIndexes[galleryKey] ?? 0;
    if (total <= 0) return 0;
    return current >= 0 && current < total ? current : 0;
  };

  const stepGallery = (galleryKey, total, delta) => {
    setGalleryIndexes((prev) => {
      const current = prev[galleryKey] ?? 0;
      const next = (current + delta + total) % total;
      return { ...prev, [galleryKey]: next };
    });
  };

  const stepPreview = (delta) => {
    setPreviewImage((prev) => {
      if (!prev || !Array.isArray(prev.images) || prev.images.length <= 1) return prev;
      const nextIndex = (prev.index + delta + prev.images.length) % prev.images.length;
      return { ...prev, index: nextIndex };
    });
  };

  return (
    <div className="min-h-screen bg-white selection:bg-emerald-100 selection:text-emerald-900">
      <section className="relative min-h-[min(90vh,980px)] xl:min-h-[85vh] overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-green-50 pt-20 md:pt-24 pb-16 md:pb-24 flex items-center">
        {renderHeroBackground()}
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/80 to-white/95"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-gray-900">
          <div className="text-center max-w-5xl mx-auto">
            <div className="inline-flex items-center px-4 py-1.5 bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 backdrop-blur-md rounded-full text-xs sm:text-sm font-semibold mb-6 animate-fade-in shadow-sm">
              <span className="flex h-2 w-2 rounded-full bg-emerald-500 mr-2 animate-pulse"></span>{tx("Kylendar Product Overview")}</div>

            <h1 className="text-[clamp(2.5rem,7vw,5.5rem)] font-bold mb-5 md:mb-6 leading-[1.05] tracking-tight animate-slide-in-up">
              <span className="bg-clip-text text-transparent bg-gradient-to-br from-gray-900 via-emerald-950 to-emerald-900">{tx("Kylendar V2")}</span>
            </h1>

            <p className="mx-auto max-w-3xl text-[clamp(1.1rem,2.5vw,1.65rem)] text-slate-700 leading-relaxed font-normal animate-slide-in-up animate-delay-100 italic opacity-90">{tx("\"A calm window into your day, designed for deep focus and clear schedule awareness.\"")}</p>

            <p className="mx-auto mt-6 md:mt-8 max-w-3xl text-[clamp(1rem,1.5vw,1.2rem)] text-slate-500 leading-relaxed animate-slide-in-up animate-delay-200">{tx("Smart 7.5-inch e-paper calendar that combines time, weather, and a rich library of specialized card components into one distraction-free display.")}</p>

            <div className="mt-12 flex flex-col items-center justify-center gap-8 animate-slide-in-up animate-delay-300">
              <div className="w-full max-w-2xl group relative">
                {/* Glowing background effect - reduced opacity and blur for softer glow */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-green-400 rounded-2xl blur-md opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>

                <a
                  href="https://kylendar.intelevia.com/us/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative block w-full"
                >
                  {/* Reduced height (py-6 instead of py-8), padding (px-8 instead of px-10), softer shadow (shadow-xl instead of shadow-2xl), slightly smaller text (text-lg sm:text-xl) */}
                  <Button className="w-full bg-emerald-600 text-white hover:bg-emerald-700 px-8 py-6 text-lg sm:text-xl font-bold rounded-2xl shadow-xl shadow-emerald-600/20 hover:shadow-emerald-600/30 hover:-translate-y-1 transition-all duration-300 overflow-hidden relative group">
                    <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"></div>
                    <span className="relative z-10 flex items-center justify-center">
                      <Monitor className="w-5 h-5 sm:w-6 sm:h-6 mr-3 transition-transform duration-300 group-hover:scale-110" />{tx("Start Designing with V2")}<ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 ml-3 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-white">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("What Kylendar V2 Delivers")}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {highlights.map((item) => (
              <Card key={item.title} className="border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="mb-4"><IconBadge icon={item.icon} large /></div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{tx(item.title)}</h3>
                  <p className="text-gray-600 leading-relaxed">{tx(item.description)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-slate-50">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <Card className="border-0 shadow-md">
            <CardContent className="p-6 sm:p-8 lg:p-10">
              <div className="flex items-center gap-2 mb-6 text-emerald-700">
                <CheckCircle2 className="w-6 h-6" />
                <h2 className="text-3xl font-bold text-gray-900">{tx("Quick Setup Flow")}</h2>
              </div>
              <div className="space-y-4">
                {setupFlow.map((step, idx) => (
                  <div key={step} className="flex items-start gap-4">
                    <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-sm font-semibold flex items-center justify-center mt-0.5 shrink-0">
                      {idx + 1}
                    </span>
                    <p className="text-gray-700 leading-relaxed text-lg">{tx(step)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <p className="text-sm text-emerald-800 leading-relaxed">
                  <strong>{tx("Workspace model:")}</strong>{tx(" one workspace maps to one device, each workspace can include multiple pages, and one device can bind only one workspace at a time.")}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6 sm:p-8 lg:p-10">
              <div className="flex items-center gap-2 mb-6 text-emerald-700">
                <Globe2 className="w-6 h-6" />
                <h2 className="text-3xl font-bold text-gray-900">{tx("Language Coverage")}</h2>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed text-lg">{tx("Kylendar V2 supports multilingual setup for shared homes, global teams, and users who prefer native-language daily context.")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-700">
                {multilingualSupport.map((language) => (
                  <div key={language} className="flex items-center gap-3 bg-white border border-emerald-100 rounded-xl px-4 py-3 shadow-sm hover:border-emerald-300 transition-colors">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <span className="font-medium">{tx(language)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-white">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("Editor Interface Overview")}</h2>
            <p className="text-gray-600 mt-4 text-lg lg:text-xl max-w-3xl mx-auto">{tx("Based on the help center editor structure: left-side component library, center live canvas, and right-side properties panel.")}</p>
          </div>

          <div className="mb-16 lg:mb-20">
            <div className="overflow-hidden rounded-[2rem] border border-emerald-100 bg-white shadow-2xl">
              <img
                src="/kylendarV2/EditorInterface.png"
                alt={tx("Kylendar editor interface overview")}
                className="w-full h-auto"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {editorInterfaceOverview.map((item) => (
              <Card key={item.title} className="border-emerald-100 shadow-sm hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start gap-5">
                    <div className="shrink-0"><IconBadge icon={item.icon} large /></div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{tx(item.title)}</h3>
                      <p className="text-gray-600 leading-relaxed text-lg">{tx(item.detail)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-12 lg:mt-16 border-0 bg-gradient-to-r from-emerald-600 to-teal-700 text-white shadow-xl rounded-[2rem] overflow-hidden">
            <CardContent className="p-8 md:p-12 lg:p-16 relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
              <h3 className="text-3xl font-bold mb-6 relative">{tx("Design Workflow Value")}</h3>
              <div className="space-y-4 text-emerald-50 relative">
                <div className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold shrink-0 mt-1">1</span>
                  <p className="text-xl">{tx("Drag cards from the left library to build a usable first structure in minutes.")}</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold shrink-0 mt-1">2</span>
                  <p className="text-xl">{tx("Use AI Magic to accelerate layout drafts, then tune details manually in the right properties panel.")}</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold shrink-0 mt-1">3</span>
                  <p className="text-xl">{tx("Keep final output consistent with real device rendering through the center live canvas workflow.")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-slate-50">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("Drag-and-Drop Layout Builder")}</h2>
            <p className="text-gray-600 mt-4 text-lg lg:text-xl max-w-3xl mx-auto">{tx("Based on Kylendar platform help guidance: layout editing focuses on visual card arrangement, widget flexibility, and fast customization without coding.")}</p>
          </div>

          <div className="mb-16 lg:mb-20 overflow-hidden rounded-[2rem] border border-emerald-100 bg-white shadow-2xl">
            <video
              className="w-full h-auto"
              src="/kylendarV2/Drag-and-DropLayout.mp4"
              autoPlay
              loop
              muted
              playsInline
              controls
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {layoutCapabilities.map((item) => (
              <Card key={item.title} className="group border-emerald-100 shadow-sm hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="relative shrink-0">
                      <div className="absolute -inset-1 rounded-xl bg-emerald-300/40 blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-[1px] shadow-sm">
                        <div className="w-full h-full rounded-[11px] bg-white flex items-center justify-center">
                          <item.icon className="w-6 h-6 text-emerald-700 transition-transform duration-300 group-hover:scale-110" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{tx(item.title)}</h3>
                      <p className="text-gray-600 leading-relaxed text-lg">{tx(item.detail)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-12 lg:mt-16 border-emerald-100 shadow-lg bg-white rounded-[2rem] overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">{tx("Card Component Style Controls")}</h3>
              <p className="text-gray-600 mb-8 leading-relaxed text-lg max-w-4xl">{tx("The card system exposes practical style options so each information block can match your visual hierarchy and keep the full dashboard clean, structured, and consistent.")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cardStyleControls.map((control) => (
                  <div key={control} className="flex items-center gap-3 px-5 py-4 rounded-xl bg-emerald-50 border border-emerald-100 transition-colors hover:bg-emerald-100">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <span className="text-gray-800 font-medium">{tx(control)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="mt-12 border-0 bg-emerald-800 text-white rounded-[2rem] overflow-hidden">
            <CardContent className="p-8 md:p-12 flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <h3 className="text-3xl font-bold mb-6">{tx("Core Selling Points")}</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">1</div>
                    <p className="text-xl text-emerald-50 font-medium whitespace-nowrap">{tx("Faster personalization with visual editing")}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">2</div>
                    <p className="text-xl text-emerald-50 font-medium whitespace-nowrap">{tx("Better readability via precise controls")}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">3</div>
                    <p className="text-xl text-emerald-50 font-medium whitespace-nowrap">{tx("High reuse value across target scenes")}</p>
                  </div>
                </div>
              </div>
              <div className="hidden lg:block w-px h-32 bg-white/20"></div>
              <div className="flex-1 text-center md:text-left">
                <p className="text-xl text-emerald-100 leading-relaxed italic uppercase tracking-wider font-semibold opacity-80">{tx("\"Production-ready design within minutes, no technical hurdles.\"")}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-white">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("AI Magic Layout Assistant")}</h2>
            <p className="text-gray-600 mt-4 text-lg lg:text-xl max-w-3xl mx-auto">{tx("Built from the AI Magic help workflow: prompt-driven generation, component-aware layout output, and full manual control after generation.")}</p>
          </div>

          <Card className="mb-16 lg:mb-20 border-0 bg-gradient-to-br from-emerald-700 to-teal-800 text-white shadow-2xl rounded-[2rem] overflow-hidden">
            <CardContent className="p-8 md:p-12 lg:p-16 relative">
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full -ml-48 -mb-48 blur-3xl"></div>
              <h3 className="text-3xl font-bold mb-8 relative">{tx("AI Magic Quick Flow")}</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
                {aiMagicFlow.map((step, idx) => (
                  <div key={step} className="flex items-start gap-5 rounded-2xl bg-white/10 border border-white/20 p-6 backdrop-blur-sm transition-all hover:bg-white/20">
                    <span className="w-10 h-10 rounded-full bg-emerald-400 text-emerald-900 text-lg font-bold flex items-center justify-center shrink-0 shadow-lg">
                      {idx + 1}
                    </span>
                    <p className="text-emerald-50 leading-relaxed text-lg">{tx(step)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="mb-16 lg:mb-20 overflow-hidden rounded-[2rem] border border-emerald-100 bg-white shadow-2xl">
            <video
              className="w-full h-auto"
              src="/kylendarV2/AIMagicLayout.mp4"
              autoPlay
              loop
              muted
              playsInline
              controls
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {aiMagicHighlights.map((item) => (
              <Card key={item.title} className="group border-emerald-100 shadow-sm hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="relative shrink-0">
                      <div className="absolute -inset-1 rounded-xl bg-emerald-300/40 blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="relative"><IconBadge icon={item.icon} large /></div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{tx(item.title)}</h3>
                      <p className="text-gray-600 leading-relaxed text-lg">{tx(item.detail)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      <section className="py-20 md:py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
        {/* Background Decorative Element */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl">
          <div className="absolute top-20 right-0 w-96 h-96 bg-emerald-100/50 rounded-full blur-3xl -z-10 animate-float"></div>
          <div className="absolute bottom-20 left-0 w-96 h-96 bg-teal-100/50 rounded-full blur-3xl -z-10 animate-float" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 lg:mb-24">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("Configuration Paths")}</h2>
            <p className="text-gray-600 mt-6 text-xl lg:text-2xl max-w-4xl mx-auto">{tx("Choose the workflow that fits your project. Both paths lead to a high-production workspace synced to your Kylendar device via the cloud.")}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-20">
            {/* Path A: The Fast Track */}
            <div className="group relative p-1 rounded-[2.5rem] bg-gradient-to-br from-emerald-100 to-emerald-200 shadow-xl hover:shadow-2xl transition-all duration-500">
              <div className="bg-white rounded-[2.3rem] p-8 md:p-12 h-full flex flex-col">
                <div className="mb-8 flex items-center justify-between">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-600 transition-transform group-hover:scale-110 group-hover:rotate-3">
                    <Zap className="w-9 h-9 fill-emerald-600/10" />
                  </div>
                  <span className="px-5 py-2 bg-emerald-600 text-white rounded-full text-sm font-bold shadow-lg shadow-emerald-600/20">{tx("Recommended for Speed")}</span>
                </div>

                <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-extrabold mb-3">{tx("Path A")}</p>
                <h3 className="text-3xl font-black text-gray-900 mb-6">{tx("Template-Assisted Start")}</h3>

                <div className="space-y-6 flex-grow">
                  <p className="text-xl text-gray-600 leading-relaxed font-medium">{tx("Skip the design phase. Browse dozens of high-production templates from ")}<span className="text-emerald-700 font-bold">{tx("Template Square")}</span>{tx(", bind your data sources, and you're live in 3 minutes.")}</p>

                  <ul className="space-y-4 pt-6 mt-6 border-t border-emerald-50">
                    <li className="flex items-center gap-3 text-gray-700 font-medium text-lg">
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" />{tx("Fast Scenario-Matching (Office, Home, Dash)")}</li>
                    <li className="flex items-center gap-3 text-gray-700 font-medium text-lg">
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" />{tx("Proven Visual Hierarchy & Layouts")}</li>
                    <li className="flex items-center gap-3 text-gray-700 font-medium text-lg">
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" />{tx("Easy Incremental Refinement")}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Path B: The Masterpiece */}
            <div className="group relative p-1 rounded-[2.5rem] bg-gradient-to-br from-teal-100 to-teal-200 shadow-xl hover:shadow-2xl transition-all duration-500">
              <div className="bg-white rounded-[2.3rem] p-8 md:p-12 h-full flex flex-col">
                <div className="mb-8 flex items-center justify-between">
                  <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center text-teal-600 transition-transform group-hover:scale-110 group-hover:-rotate-3">
                    <PenTool className="w-9 h-9 fill-teal-600/10" />
                  </div>
                  <span className="px-5 py-2 bg-slate-800 text-white rounded-full text-sm font-bold">{tx("Full Creative Control")}</span>
                </div>

                <p className="text-xs uppercase tracking-[0.2em] text-teal-700 font-extrabold mb-3">{tx("Path B")}</p>
                <h3 className="text-3xl font-black text-gray-900 mb-6">{tx("Full Custom Start")}</h3>

                <div className="space-y-6 flex-grow">
                  <p className="text-xl text-gray-600 leading-relaxed font-medium">{tx("Start from a blank 800x480 canvas. Place structured card components with defined size profiles, then fine-tune every margin, corner radius, and visual detail to craft your ideal layout.")}</p>

                  <ul className="space-y-4 pt-6 mt-6 border-t border-teal-50">
                    <li className="flex items-center gap-3 text-gray-700 font-medium text-lg">
                      <CheckCircle2 className="w-6 h-6 text-teal-500" />{tx("Card-Based Structured Placement")}</li>
                    <li className="flex items-center gap-3 text-gray-700 font-medium text-lg">
                      <CheckCircle2 className="w-6 h-6 text-teal-500" />{tx("Deep Component Style Personalization")}</li>
                    <li className="flex items-center gap-3 text-gray-700 font-medium text-lg">
                      <CheckCircle2 className="w-6 h-6 text-teal-500" />{tx("Pixel-Perfect Branding & Arrangement")}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="relative p-1 rounded-[3rem] bg-gradient-to-r from-emerald-600 via-teal-700 to-emerald-800 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 grid-pattern-overlay opacity-10"></div>
            <div className="relative p-8 md:p-12 lg:p-16 text-white text-center">
              <h3 className="text-3xl md:text-4xl font-black mb-10">{tx("Common Final Steps to Launch")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                <div className="absolute top-1/2 left-0 right-0 h-px bg-white/10 hidden md:block -translate-y-1/2"></div>
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
                  <div className="w-12 h-12 rounded-full bg-white text-emerald-800 font-black flex items-center justify-center mx-auto mb-6 shadow-lg">1</div>
                  <h4 className="text-xl font-bold mb-3">{tx("Save Binding")}</h4>
                  <p className="opacity-80">{tx("Link your workspace to your physical device.")}</p>
                </div>
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
                  <div className="w-12 h-12 rounded-full bg-white text-emerald-800 font-black flex items-center justify-center mx-auto mb-6 shadow-lg">2</div>
                  <h4 className="text-xl font-bold mb-3">{tx("Cloud Sync")}</h4>
                  <p className="opacity-80">{tx("Design data automatically moves to the production cloud.")}</p>
                </div>
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
                  <div className="w-12 h-12 rounded-full bg-white text-emerald-800 font-black flex items-center justify-center mx-auto mb-6 shadow-lg">3</div>
                  <h4 className="text-xl font-bold mb-3">{tx("Live Refresh")}</h4>
                  <p className="opacity-80">{tx("Device pulls and displays the production-ready layout.")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-white">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("Feature Deep Dive")}</h2>
            <p className="text-gray-600 mt-4 text-lg lg:text-xl max-w-3xl mx-auto">{tx("Expanded feature details based on common help-center usage and setup guidance.")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {featureDetails.map((item) => (
              <Card key={item.title} className="border-emerald-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="mb-6"><IconBadge icon={item.icon} large /></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{tx(item.title)}</h3>
                  <p className="text-gray-600 leading-relaxed text-base">{tx(item.description)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-white">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("Canvas Size and Card Size System")}</h2>
            <p className="text-gray-600 mt-4 text-lg lg:text-xl max-w-3xl mx-auto">{tx("Kylendar layout editing runs on a fixed 800 x 480 canvas with standardized card size profiles for predictable composition.")}</p>
          </div>

          <Card className="border-0 bg-emerald-600 text-white mb-12 rounded-[2rem] overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <h3 className="text-3xl font-bold mb-4">{tx("Design Canvas: 800 x 480")}</h3>
              <p className="text-emerald-50 text-xl leading-relaxed max-w-4xl">{tx("The editor and device rendering target the same 800 x 480 canvas, reducing layout mismatch and making drag-and-drop placement production-ready from the first design pass.")}</p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {canvasAndSizeProfiles.map((profile) => (
              <Card key={profile.size} className="border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900">{tx(profile.size)}</h3>
                  <p className="text-emerald-700 font-bold mt-1">{tx(profile.pixels)}</p>
                  <p className="text-gray-600 mt-2 leading-relaxed">{tx(profile.usage)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-slate-50">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("Configuration Guide")}</h2>
            <p className="text-gray-600 mt-4 text-lg lg:text-xl max-w-3xl mx-auto">{tx("Practical configuration model inspired by Kylendar help definitions for component cards and layout templates.")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {configurationGuide.map((item) => (
              <Card key={item.key} className="border-emerald-100 shadow-sm hover:shadow-md transition-shadow bg-white/50 backdrop-blur-sm">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{tx(item.key)}</h3>
                  <p className="text-gray-700 leading-relaxed text-lg">{tx(item.value)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-white">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("Connectivity and Maintenance")}</h2>
            <p className="text-gray-600 mt-4 text-lg lg:text-xl max-w-3xl mx-auto">{tx("Core support details aligned with Kylendar help guidance for deployment, updates, and ongoing use.")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {supportPoints.map((item) => (
              <Card key={item.title} className="border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-8">
                  <div className="mb-6"><IconBadge icon={item.icon} large /></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{tx(item.title)}</h3>
                  <p className="text-gray-600 leading-relaxed text-lg">{tx(item.description)}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-12 lg:mt-16 border-0 bg-emerald-50 rounded-[2rem] overflow-hidden">
            <CardContent className="p-8 md:p-12 lg:p-16">
              <div className="flex items-start gap-6">
                <div className="shrink-0">
                  <IconBadge icon={Newspaper} large />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("Daily Information Without Overload")}</h3>
                  <p className="text-gray-700 leading-relaxed text-xl max-w-4xl">{tx("Kylendar V2 is designed for users who want ambient information instead of another interactive screen. It keeps the essentials visible and predictable, making routines easier for families, remote teams, and anyone reducing phone-first habits.")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-100/30 rounded-full blur-3xl -z-10 animate-float"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-100/30 rounded-full blur-3xl -z-10 animate-float" style={{ animationDelay: '1.5s' }}></div>

        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-24 animate-slide-in-up">
            <div className="inline-flex items-center px-4 py-1.5 bg-amber-500/10 text-amber-700 border border-amber-500/20 backdrop-blur-md rounded-full text-xs sm:text-sm font-bold mb-6 shadow-sm">
              <Zap className="w-4 h-4 mr-2 fill-amber-500" />{tx("TOP SELECTIONS")}</div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 tracking-tight mb-8">{tx("Popular Recommended Cards")}</h2>
            <div className="max-w-4xl mx-auto space-y-6">
              <p className="text-gray-600 text-xl lg:text-2xl leading-relaxed font-medium">{tx("These hand-picked components represent our most versatile and high-impact widgets. Designed for immediate value and minimal setup friction.")}</p>
              <div className="flex flex-wrap justify-center gap-6 pt-4">
                <div className="flex items-center gap-2 text-emerald-700 font-bold bg-emerald-50 px-4 py-2 rounded-xl border border-emerald-100">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>{tx("High Visibility")}</span>
                </div>
                <div className="flex items-center gap-2 text-blue-700 font-bold bg-blue-50 px-4 py-2 rounded-xl border border-blue-100">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>{tx("Rapid Configuration")}</span>
                </div>
                <div className="flex items-center gap-2 text-purple-700 font-bold bg-purple-50 px-4 py-2 rounded-xl border border-purple-100">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>{tx("Essential Daily Context")}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
            {popularRecommendedCards.map((item, idx) => {
              const galleryKey = item.ids.join('|');
              const hasGallery = Array.isArray(item.images) && item.images.length > 0;
              const currentIndex = hasGallery ? getGalleryIndex(galleryKey, item.images.length) : 0;
              const currentImage = hasGallery ? item.images[currentIndex] : null;

              return (
                <div
                  key={item.name}
                  className="group relative rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 animate-slide-in-up"
                  style={{ animationDelay: `${idx * 150}ms` }}
                >
                  {/* Recommended Badge */}
                  <div className="absolute -top-3 -right-3 z-10 bg-amber-500 text-white text-[10px] font-black px-3 py-1 rounded-full shadow-lg transform rotate-12 group-hover:rotate-0 transition-transform duration-300">{tx("MUST HAVE")}</div>

                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="text-base font-bold text-gray-900 leading-tight group-hover:text-emerald-700 transition-colors">
                      {tx(item.name)}
                    </div>
                    <span className="inline-flex self-start shrink-0 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">{tx("Sizes ")}{tx(getComponentSizes(item.ids))}
                    </span>
                  </div>

                  <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-3 group-hover:bg-emerald-50/30 group-hover:border-emerald-100 transition-colors duration-300">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 font-semibold">{tx("Configuration")}</p>
                    <p className="text-sm text-slate-600 leading-relaxed font-medium">
                      {tx(getComponentConfigHint(item.ids))}
                    </p>
                  </div>

                  <div className="mt-3 border-l-2 border-emerald-200 pl-3">
                    <p className="text-sm text-gray-700 leading-relaxed font-medium">
                      {tx(optimizeComponentDescription(item.description))}
                    </p>
                  </div>

                  {hasGallery ? (
                    <div className="mt-4">
                      <div className="relative group/gallery">
                        <button
                          type="button"
                          className="block w-full overflow-hidden rounded-xl border border-emerald-100 bg-slate-50 transition-all duration-300 hover:shadow-md focus:outline-none"
                          onClick={() =>
                            setPreviewImage({
                              images: item.images,
                              index: currentIndex,
                              altBase: `${item.name} preview`
                            })
                          }
                        >
                          <img
                            className="w-full h-auto transform transition-transform duration-700 group-hover:scale-105"
                            src={currentImage}
                            alt={tx(`${item.name} preview ${currentIndex + 1}`)}
                            loading="lazy"
                          />
                        </button>
                        <div className="absolute inset-x-2 top-1/2 -translate-y-1/2 flex justify-between opacity-0 group-hover/gallery:opacity-100 transition-opacity duration-300">
                          <button
                            type="button"
                            className="rounded-full bg-white/95 p-1.5 text-emerald-700 shadow-sm transition-all hover:bg-emerald-600 hover:text-white"
                            onClick={() => stepGallery(galleryKey, item.images.length, -1)}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded-full bg-white/95 p-1.5 text-emerald-700 shadow-sm transition-all hover:bg-emerald-600 hover:text-white"
                            onClick={() => stepGallery(galleryKey, item.images.length, 1)}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-2 text-center text-xs text-slate-500">
                          {currentIndex + 1} / {tx(item.images.length)}
                        </div>
                      </div>
                    </div>
                  ) : item.image ? (
                    <button
                      type="button"
                      className="mt-4 block w-full overflow-hidden rounded-xl border border-emerald-100 bg-slate-50 transition-all duration-300 hover:shadow-md focus:outline-none"
                      onClick={() => setPreviewImage({ src: item.image, alt: `${item.name} preview` })}
                    >
                      <img
                        className="w-full h-auto transform transition-transform duration-700 group-hover:scale-105"
                        src={item.image}
                        alt={tx(`${item.name} preview`)}
                        loading="lazy"
                      />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="mt-16 bg-white/50 backdrop-blur-sm border border-emerald-100 rounded-[2.5rem] p-8 md:p-12 text-center animate-slide-in-up" style={{ animationDelay: '1000ms' }}>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{tx("Why these cards?")}</h3>
            <p className="text-gray-600 text-lg max-w-4xl mx-auto leading-relaxed">{tx("Our data shows that these components are used in over 90% of active Kylendar workspaces. They provide the foundation for most personal dashboards, balancing efficiency with the zero-distraction ethos of E-Ink.")}</p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-white">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("Component Reference: Full Library")}</h2>
            <p className="text-gray-600 mt-4 text-lg lg:text-xl max-w-3xl mx-auto">{tx("Complete component coverage aligned with the Kylendar help center Component Reference structure, including but not limited to time, weather, calendar schedules, headlines, and world time cards.")}</p>
          </div>

          <Card className="mb-12 border-0 bg-gradient-to-r from-emerald-600 to-teal-700 text-white shadow-xl rounded-[2rem] overflow-hidden">
            <CardContent className="p-8 lg:p-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                <div className="border-l-2 border-white/20 pl-6">
                  <p className="text-emerald-100 text-xs uppercase tracking-widest font-bold mb-1">{tx("Canvas")}</p>
                  <p className="text-3xl font-bold">800 x 480</p>
                </div>
                <div className="border-l-2 border-white/20 pl-6">
                  <p className="text-emerald-100 text-xs uppercase tracking-widest font-bold mb-1">{tx("Card Sizes")}</p>
                  <p className="text-3xl font-bold">{tx("Size 0 to Size 5")}</p>
                </div>
                <div className="border-l-2 border-white/20 pl-6">
                  <p className="text-emerald-100 text-xs uppercase tracking-widest font-bold mb-1">{tx("Reference Focus")}</p>
                  <p className="text-3xl font-bold">{tx("Clarity + Choice")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            {componentReferenceSections.map((section) => (
              <Card key={section.category} className="border-emerald-100 shadow-sm overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">{tx(section.category)}</h3>
                    <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 text-xs font-semibold">
                      {tx(section.items.length)}{tx(" components")}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
                    {section.items.map((item) => {
                      const galleryKey = item.ids.join('|');
                      const hasGallery = Array.isArray(item.images) && item.images.length > 0;
                      const currentIndex = hasGallery ? getGalleryIndex(galleryKey, item.images.length) : 0;
                      const currentImage = hasGallery ? item.images[currentIndex] : null;

                      return (
                        <div
                          key={item.name}
                          className="group rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                            <div className="text-base font-bold text-gray-900 leading-tight">{tx(item.name)}</div>
                            <span className="inline-flex self-start shrink-0 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">{tx("Sizes ")}{tx(getComponentSizes(item.ids))}
                            </span>
                          </div>
                          <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
                            <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1 font-semibold">{tx("Configuration")}</p>
                            <p className="text-sm text-slate-600 leading-relaxed">{tx(getComponentConfigHint(item.ids))}</p>
                          </div>
                          <div className="mt-3 border-l-2 border-emerald-200 pl-3">
                            <p className="text-sm text-gray-700 leading-relaxed">{tx(optimizeComponentDescription(item.description))}</p>
                          </div>
                          {hasGallery ? (
                            <div className="mt-4">
                              <div className="relative">
                                <button
                                  type="button"
                                  className="block w-full overflow-hidden rounded-xl border border-emerald-100 bg-slate-50 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
                                  onClick={() =>
                                    setPreviewImage({
                                      images: item.images,
                                      index: currentIndex,
                                      altBase: `${item.name} preview`
                                    })
                                  }
                                  aria-label={tx(`Open larger preview for ${item.name}`)}
                                >
                                  <img
                                    className="w-full h-auto"
                                    src={currentImage}
                                    alt={tx(`${item.name} preview ${currentIndex + 1}`)}
                                    loading="lazy"
                                  />
                                </button>
                                <button
                                  type="button"
                                  className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 text-emerald-700 shadow-sm transition-colors hover:bg-white"
                                  onClick={() => stepGallery(galleryKey, item.images.length, -1)}
                                  aria-label={tx(`Previous ${item.name} image`)}
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 text-emerald-700 shadow-sm transition-colors hover:bg-white"
                                  onClick={() => stepGallery(galleryKey, item.images.length, 1)}
                                  aria-label={tx(`Next ${item.name} image`)}
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="mt-2 text-center text-xs text-slate-500">
                                {currentIndex + 1}/{tx(item.images.length)}
                              </div>
                            </div>
                          ) : item.image ? (
                            <button
                              type="button"
                              className="mt-4 block w-full overflow-hidden rounded-xl border border-emerald-100 bg-slate-50 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
                              onClick={() => setPreviewImage({ src: item.image, alt: `${item.name} preview` })}
                              aria-label={tx(`Open larger preview for ${item.name}`)}
                            >
                              <img
                                className="w-full h-auto"
                                src={item.image}
                                alt={tx(`${item.name} preview`)}
                                loading="lazy"
                              />
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 lg:py-32 bg-slate-50">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">{tx("Support Snapshot")}</h2>
            <p className="text-gray-600 mt-4 text-lg lg:text-xl max-w-3xl mx-auto">{tx("Quick access to Kylendar platform help channels for troubleshooting and setup assistance.")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 mb-16 lg:mb-20">
            {supportSnapshot.map((item) => (
              <Card key={item.title} className="group border-emerald-100 shadow-sm hover:shadow-lg transition-all duration-300 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="shrink-0"><IconBadge icon={item.icon} large /></div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{tx(item.title)}</h3>
                      <p className="text-gray-600 leading-relaxed text-lg">{tx(item.body)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full max-w-4xl mx-auto">
            <Link to="/help-center" className="w-full sm:w-auto">
              <Button className="w-full bg-emerald-600 text-white hover:bg-emerald-700 px-10 py-7 text-xl font-bold rounded-[2rem] shadow-xl shadow-emerald-600/20 hover:shadow-emerald-600/30 hover:-translate-y-1.5 transition-all duration-300 relative group overflow-hidden">
                <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                <span className="relative z-10 flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 mr-3 transition-transform group-hover:rotate-12" />{tx("View Help Center")}</span>
              </Button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full border-2 border-emerald-100 bg-white text-emerald-800 hover:border-emerald-300 hover:bg-emerald-50/50 px-10 py-7 text-xl font-bold rounded-[2rem] shadow-lg shadow-emerald-100/50 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300">
                <span className="flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 mr-3 text-emerald-600" />{tx("Contact Sales")}</span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {
        previewImage ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6"
            onClick={() => setPreviewImage(null)}
          >
            <div
              className="relative w-full max-w-5xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mb-3 flex justify-end">
                <button
                  type="button"
                  className="rounded-full bg-white/90 px-3 py-1.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-white"
                  onClick={() => setPreviewImage(null)}
                >{tx("Close")}</button>
              </div>
              <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="relative">
                  <img
                    className="max-h-[85vh] w-full object-contain bg-slate-100"
                    src={Array.isArray(previewImage.images) ? previewImage.images[previewImage.index] : previewImage.src}
                    alt={tx(Array.isArray(previewImage.images) ? `${previewImage.altBase} ${previewImage.index + 1}` : previewImage.alt)}
                  />
                  {Array.isArray(previewImage.images) && previewImage.images.length > 1 ? (
                    <>
                      <button
                        type="button"
                        className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-emerald-700 shadow-sm transition-colors hover:bg-white"
                        onClick={() => stepPreview(-1)}
                        aria-label={tx("Previous preview image")}
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-emerald-700 shadow-sm transition-colors hover:bg-white"
                        onClick={() => stepPreview(1)}
                        aria-label={tx("Next preview image")}
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  ) : null}
                </div>
                {Array.isArray(previewImage.images) && previewImage.images.length > 1 ? (
                  <div className="flex justify-center border-t border-slate-200 bg-white px-4 py-2">
                    <span className="text-xs text-slate-500">
                      {previewImage.index + 1} / {tx(previewImage.images.length)}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null
      }
    </div >
  );
}
